/**
 * EvaluateCampaignModal.jsx — v2.2.0
 *
 * Changes:
 *   - Agency: Revenue optional (3 paths), added TargetCPL + TargetCPA fields, removed targetRoas
 *   - Store:  MarginPct optional (warning badge if empty, not a hard block)
 *   - Service: ConversionPct label clarified → "lead → paying customer", live CAC preview
 *   - Currency: SAR symbol (ر.س) on all placeholders / hints
 *   - Validation: agency checks at least one of revenue|leads|conversions
 */
import { useState, useEffect } from "react";
import { functions, db } from "../../firebaseConfig";
import { channelDisplay } from "../../lib/channels";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import { FiAlertCircle, FiInfo, FiLock, FiCheckCircle, FiTarget, FiRefreshCw } from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useQuotas } from "../../hooks/useQuotas";
import { useDarkMode } from "../../hooks/useDarkMode";
import { useRoles } from "../../hooks/useRoles";

const CHANNEL_HINTS = {
  tiktok:   { en: "TikTok: Focus on impressions + clicks for engagement metrics", ar: "تيك توك: ركّز على مرات الظهور والنقرات لقياس التفاعل" },
  snapchat: { en: "Snapchat: Swipe-up rate and story views drive conversions",     ar: "سناب شات: معدل السحب لأعلى ومشاهدات القصة تقود التحويلات" },
  google:   { en: "Google Ads: CTR and Quality Score directly impact ROAS",        ar: "جوجل: نسبة النقر إلى الظهور ونقاط الجودة تؤثر على عائد الإنفاق" },
  meta:     { en: "Meta: Include all placements — Feed, Stories, Reels — in spend", ar: "ميتا: أدرج جميع مواضع الإعلانات — الخلاصة والقصص والريلز — في الإنفاق" },
};

const BIZ_TYPES = [
  { key: "agency",  icon: "🎯", labelKey: "business_type_agency"  },
  { key: "store",   icon: "🛒", labelKey: "business_type_store"   },
  { key: "service", icon: "💼", labelKey: "business_type_service" },
];

const EvaluateCampaignModal = ({ campaign, businessType: propBizType, onClose, onSuccess }) => {
  const { t, lang }                                        = useLanguage();
  const { canSelectMode, getAllowedModes, isModeAllowed }  = useFeatureFlags();
  const { canEvaluate }                                    = useQuotas();
  const { userData }                                       = useAuth();
  const { darkMode }                                       = useDarkMode();
  // Only system admins (superadmin role) are allowed to override the
  // business type for a tenant. Normal users — even tenant admins — see
  // the type that was set during tenant creation as read-only. This
  // matches the "Companies" tab visibility in /admin which is also gated
  // to superadmin.
  const { isSuperAdmin }                                   = useRoles();

  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState("");
  const [decisionMode,  setDecisionMode]  = useState("balanced");
  const [days,          setDays]          = useState(7);
  const [bizType,       setBizType]       = useState(propBizType || "agency");
  const [bizLoading,    setBizLoading]    = useState(false);
  const [showBizPicker, setShowBizPicker] = useState(false);

  // Shared
  const [cost,         setCost]          = useState("");

  // Agency
  const [revenue,      setRevenue]       = useState("");
  const [impressions,  setImpressions]   = useState("");
  const [clicks,       setClicks]        = useState("");
  const [leads,        setLeads]         = useState("");
  const [conversions,  setConversions]   = useState("");
  const [targetCPL,    setTargetCPL]     = useState("");
  const [targetCPA,    setTargetCPA]     = useState("");

  // Store
  const [storeRevenue, setStoreRevenue]  = useState("");
  const [margin,       setMargin]        = useState("");
  const [fees,         setFees]          = useState("");
  const [shipping,     setShipping]      = useState("");
  const [returnsPct,   setReturnsPct]    = useState("");

  // Service
  const [svcLeads,     setSvcLeads]      = useState("");
  const [convPct,      setConvPct]       = useState("");
  const [avgTicket,    setAvgTicket]     = useState("");
  const [serviceCost,  setServiceCost]   = useState("");

  const allowedModes = getAllowedModes();
  const channelHint  = CHANNEL_HINTS[campaign?.channel]?.[lang === "ar" ? "ar" : "en"];
  const isAr         = lang === "ar";

  useEffect(() => {
    if (!canSelectMode() || !isModeAllowed(decisionMode)) setDecisionMode("balanced");
  }, []);

  useEffect(() => {
    if (propBizType) { setBizType(propBizType); return; }
    const load = async () => {
      if (!userData?.tenantId) return;
      setBizLoading(true);
      try {
        const snap = await getDoc(doc(db, "tenants", userData.tenantId));
        if (snap.exists()) setBizType(snap.data().businessType || "agency");
      } catch {} finally { setBizLoading(false); }
    };
    load();
  }, [propBizType, userData?.tenantId]);

  // Detect agency path for live indicator
  const agencyPath = () => {
    if (revenue     && parseFloat(revenue)     > 0) return "revenue";
    if (conversions && parseInt(conversions)   > 0) return "conversion";
    if (leads       && parseInt(leads)         > 0) return "leadgen";
    return null;
  };

  // Service live calculations
  const estCustomers = svcLeads && convPct
    ? Math.round(parseInt(svcLeads || 0) * (parseFloat(convPct || 0) / 100))
    : null;
  const estCAC = estCustomers > 0 && cost
    ? (parseFloat(cost || 0) / estCustomers).toFixed(0)
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const quota = await canEvaluate();
    if (!quota.available) { setError(quota.message || t("quota_exceeded_hint")); return; }

    if (!cost || parseFloat(cost) <= 0) { setError(t("error_cost_required")); return; }

    let inputs = {};

    if (bizType === "agency") {
      const hasRevenue     = revenue     && parseFloat(revenue)     > 0;
      const hasLeads       = leads       && parseInt(leads)         > 0;
      const hasConversions = conversions && parseInt(conversions)   > 0;

      if (!hasRevenue && !hasLeads && !hasConversions) {
        setError(isAr
          ? "الوكالة: يلزم إدخال واحد على الأقل — الإيرادات أو العملاء المحتملين أو التحويلات"
          : "Agency requires at least one of: Revenue, Leads, or Conversions");
        return;
      }

      inputs = {
        cost: parseFloat(cost),
        ...(hasRevenue     ? { revenue:     parseFloat(revenue)   } : {}),
        ...(impressions    ? { impressions: parseInt(impressions) } : {}),
        ...(clicks         ? { clicks:      parseInt(clicks)      } : {}),
        ...(hasLeads       ? { leads:       parseInt(leads)       } : {}),
        ...(hasConversions ? { conversions: parseInt(conversions) } : {}),
        ...(targetCPL      ? { targetCPL:   parseFloat(targetCPL)} : {}),
        ...(targetCPA      ? { targetCPA:   parseFloat(targetCPA)} : {}),
      };

    } else if (bizType === "store") {
      if (!storeRevenue || parseFloat(storeRevenue) < 0) {
        setError(t("error_revenue_required")); return;
      }
      inputs = {
        cost:    parseFloat(cost),
        revenue: parseFloat(storeRevenue),
        ...(margin     ? { margin_pct:  parseFloat(margin)     } : {}),
        ...(fees       ? { fees:        parseFloat(fees)       } : {}),
        ...(shipping   ? { shipping:    parseFloat(shipping)   } : {}),
        ...(returnsPct ? { returns_pct: parseFloat(returnsPct) } : {}),
      };

    } else if (bizType === "service") {
      if (!svcLeads   || parseInt(svcLeads)     <= 0)  { setError(t("error_leads_conversions_required")); return; }
      if (!convPct    || parseFloat(convPct)    <= 0)  { setError(t("error_leads_conversions_required")); return; }
      if (parseFloat(convPct) > 100)                   { setError(t("error_conversions_exceed_leads")); return; }
      if (!avgTicket  || parseFloat(avgTicket)  <= 0)  { setError(t("avg_ticket") + " " + t("error_cost_required")); return; }

      inputs = {
        cost:           parseFloat(cost),
        leads:          parseInt(svcLeads),
        conversion_pct: parseFloat(convPct),
        avg_ticket:     parseFloat(avgTicket),
        ...(serviceCost ? { service_cost: parseFloat(serviceCost) } : {}),
      };
    }

    setLoading(true);
    try {
      const fn  = httpsCallable(functions, "evaluateCampaign");
      const res = await fn({
        campaignId:   campaign.id,
        channel:      campaign.channel,
        decisionMode: canSelectMode() ? decisionMode : "balanced",
        days:         parseInt(days),
        inputs,
        businessType: bizType,
        lang,
      });
      onSuccess(res.data);
    } catch (err) {
      const msg = err.message || "";
      setError(msg.includes("quota") || msg.includes("limit") ? t("quota_exceeded_hint") : msg || t("error"));
    } finally { setLoading(false); }
  };

  const inputCls = `w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 text-left ${
    darkMode
      ? "bg-gray-700/50 border-gray-600 text-white placeholder-gray-500"
      : "bg-gray-100 border-gray-300 text-gray-900 placeholder-gray-400"
  }`;

  const Label = ({ text, required, optional }) => (
    <label className={`block text-sm font-medium mb-2 text-left ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
      {text}
      {required && <span className="text-red-400 ml-1">*</span>}
      {optional && (
        <span className={`ml-1 text-xs font-normal ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
          ({isAr ? "اختياري" : "optional"})
        </span>
      )}
    </label>
  );

  const HintBox = ({ children }) => (
    <div className={`${darkMode ? "bg-gray-700/20 border-gray-700 text-gray-400" : "bg-gray-50 border-gray-200 text-gray-500"} border rounded-xl p-3 text-xs leading-relaxed`}>
      {children}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} border rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto`}
      >
        <div className="p-6 md:p-8">

          {/* ── Header ──────────────────────────────────────── */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>{t("evaluate_campaign_title")}</h2>
              <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-sm mt-1`}>
                <FiTarget className="inline mr-1 text-blue-400" />
                {campaign.name}
                {campaign.channel && (
                  <span className={darkMode ? "text-gray-500" : "text-gray-400"}>
                    {" · "}{channelDisplay(campaign.channel, lang)}
                  </span>
                )}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-4">
              <button
                type="button"
                // Only superadmins can open the picker. For everyone else
                // the button is disabled and we show a lock icon.
                onClick={() => isSuperAdmin && setShowBizPicker(!showBizPicker)}
                disabled={!isSuperAdmin}
                title={isSuperAdmin
                  ? t("change_business_type")
                  : (isAr ? "نوع العمل محدد من قبل مسؤول النظام" : "Business type is set by the system administrator")}
                className={`flex items-center gap-2 border px-3 py-1.5 rounded-xl text-sm transition-colors ${
                  !isSuperAdmin ? "cursor-not-allowed opacity-70" : ""
                } ${
                  darkMode ? "bg-gray-700/60 hover:bg-gray-700 border-gray-600" : "bg-gray-100 hover:bg-gray-200 border-gray-300"
                }`}
              >
                {bizLoading
                  ? <FiRefreshCw className="animate-spin text-gray-400" />
                  : <span>{BIZ_TYPES.find(b => b.key === bizType)?.icon}</span>
                }
                <span className={`${darkMode ? "text-gray-300" : "text-gray-700"} font-medium`}>{t(`business_type_${bizType}`)}</span>
                {/* Show lock icon to non-superadmins so the disabled
                    state is visible at a glance */}
                {!isSuperAdmin && <FiLock className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`} />}
              </button>
              <span className={`${darkMode ? "text-gray-600" : "text-gray-400"} text-xs`}>
                {isSuperAdmin
                  ? t("auto_detected_label")
                  : (isAr ? "محدد من النظام" : "Set by system")}
              </span>
            </div>
          </div>

          {/* ── Business type picker ─────────────────────────── */}
          {/* Only superadmins ever see this open — see the trigger
              button above which is disabled for normal users. We still
              keep the per-button disabled state inside the grid as a
              second layer in case the picker is ever surfaced
              programmatically in the future. */}
          <AnimatePresence>
            {showBizPicker && isSuperAdmin && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden mb-6"
              >
                <div className={`${darkMode ? "bg-gray-700/30 border-gray-600" : "bg-gray-50 border-gray-200"} border rounded-xl p-4`}>
                  <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-xs mb-3`}>{t("select_business_type_hint")}</p>
                  <div className="grid grid-cols-3 gap-2">
                    {BIZ_TYPES.map(b => {
                      const isSelected = bizType === b.key;
                      return (
                        <button
                          key={b.key}
                          type="button"
                          onClick={() => { setBizType(b.key); setShowBizPicker(false); }}
                          className={`flex flex-col items-center gap-1 p-3 rounded-xl border transition-all text-sm font-medium ${
                            isSelected
                              ? "bg-blue-600 border-blue-500 text-white"
                              : darkMode
                                ? "bg-gray-700/40 border-gray-600 text-gray-300 hover:border-blue-500/50"
                                : "bg-white border-gray-300 text-gray-700 hover:border-blue-400"
                          }`}
                        >
                          <span className="text-2xl">{b.icon}</span>
                          <span>{t(b.labelKey)}</span>
                          {isSelected && <FiCheckCircle className="text-white text-xs" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Error ───────────────────────────────────────── */}
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-xl mb-5 flex items-start gap-3">
              <FiAlertCircle className="flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">

            {/* ── Decision Mode ──────────────────────────────── */}
            <div>
              <Label text={t("decision_mode")} />
              {canSelectMode() ? (
                <>
                  <select value={decisionMode} onChange={e => setDecisionMode(e.target.value)} className={inputCls}>
                    {allowedModes.includes("conservative") && <option value="conservative">{t("decision_mode_conservative")}</option>}
                    {allowedModes.includes("balanced")     && <option value="balanced">{t("decision_mode_balanced")}</option>}
                    {allowedModes.includes("aggressive")   && <option value="aggressive">{t("decision_mode_aggressive")}</option>}
                  </select>
                  <div className={`flex items-start gap-2 mt-2 text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                    <FiInfo className="flex-shrink-0 mt-0.5 text-blue-400" />
                    <span>{{ conservative: t("mode_conservative_desc"), balanced: t("mode_balanced_desc"), aggressive: t("mode_aggressive_desc") }[decisionMode]}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="relative">
                    <input type="text" value={t("decision_mode_balanced_fixed")} disabled className={`${inputCls} cursor-not-allowed opacity-60`} />
                  </div>
                  <div className="mt-2 bg-blue-500/10 border border-blue-500/30 p-3 rounded-xl flex items-start gap-2">
                    <FiInfo className="text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-blue-300 text-xs">{t("mode_locked_msg")}</p>
                  </div>
                </>
              )}
            </div>

            {/* ── Evaluation period ──────────────────────────── */}
            <div>
              <Label text={t("evaluation_period")} />
              <input type="number" value={days} onChange={e => setDays(e.target.value)} min="1" max="90" className={inputCls} required />
              <p className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs mt-1`}>{t("evaluation_period_hint")}</p>
            </div>

            {/* ── Metrics section ────────────────────────────── */}
            <div className={`border-t ${darkMode ? "border-gray-700" : "border-gray-200"} pt-5`}>
              <div className="flex items-center gap-2 mb-5">
                <h3 className={`${darkMode ? "text-white" : "text-gray-900"} font-semibold`}>{t("campaign_metrics")}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? "bg-gray-700 text-gray-400" : "bg-gray-200 text-gray-600"}`}>
                  {BIZ_TYPES.find(b => b.key === bizType)?.icon} {t(`business_type_${bizType}`)}
                </span>
              </div>

              {channelHint && (
                <div className="flex items-start gap-2 mb-4 bg-yellow-500/10 border border-yellow-500/20 p-3 rounded-xl text-xs text-yellow-300">
                  <FiInfo className="flex-shrink-0 mt-0.5" /><span>{channelHint}</span>
                </div>
              )}

              {/* Ad Spend — always shown */}
              <div className="mb-4">
                <Label text={t("ad_spend")} required />
                <input type="number" value={cost} onChange={e => setCost(e.target.value)} step="0.01" min="0" className={inputCls} placeholder="0 ر.س" required />
              </div>

              <AnimatePresence mode="wait">
                <motion.div key={bizType} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>

                  {/* ════════════ AGENCY ════════════ */}
                  {bizType === "agency" && (
                    <div className="space-y-4">

                      {/* Path hint */}
                      <div className="bg-blue-500/10 border border-blue-500/20 p-3 rounded-xl flex items-start gap-2 text-xs text-blue-300">
                        <FiInfo className="flex-shrink-0 mt-0.5" />
                        <span>
                          {isAr
                            ? "أدخل الإيرادات للمسار الكامل، أو العملاء المحتملين فقط لمسار Lead Gen، أو التحويلات فقط لمسار الاكتساب."
                            : "Enter Revenue for the full path, Leads only for Lead Gen, or Conversions only for conversion-based evaluation."}
                        </span>
                      </div>

                      {/* Revenue — optional */}
                      <div>
                        <Label text={t("revenue")} optional />
                        <input type="number" value={revenue} onChange={e => setRevenue(e.target.value)} step="0.01" min="0" className={inputCls} placeholder="0 ر.س" />
                      </div>

                      {/* Impressions + Clicks */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label text={t("impressions")} optional />
                          <input type="number" value={impressions} onChange={e => setImpressions(e.target.value)} min="0" className={inputCls} placeholder={t("impressions_placeholder")} />
                        </div>
                        <div>
                          <Label text={t("clicks")} optional />
                          <input type="number" value={clicks} onChange={e => setClicks(e.target.value)} min="0" className={inputCls} placeholder={t("clicks_placeholder")} />
                        </div>
                      </div>

                      {/* Leads + Conversions */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label text={t("leads")} optional />
                          <input type="number" value={leads} onChange={e => setLeads(e.target.value)} min="0" className={inputCls} placeholder="50" />
                        </div>
                        <div>
                          <Label text={t("conversions")} optional />
                          <input type="number" value={conversions} onChange={e => setConversions(e.target.value)} min="0" className={inputCls} placeholder="10" />
                        </div>
                      </div>

                      {/* TargetCPL + TargetCPA — NEW */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label text={isAr ? "هدف تكلفة العميل المحتمل (CPL)" : "Target CPL"} optional />
                          <input type="number" value={targetCPL} onChange={e => setTargetCPL(e.target.value)} step="0.01" min="0" className={inputCls} placeholder="25 ر.س" />
                        </div>
                        <div>
                          <Label text={isAr ? "هدف تكلفة الاكتساب (CPA)" : "Target CPA"} optional />
                          <input type="number" value={targetCPA} onChange={e => setTargetCPA(e.target.value)} step="0.01" min="0" className={inputCls} placeholder="100 ر.س" />
                        </div>
                      </div>

                      {/* Live path indicator */}
                      {agencyPath() && (
                        <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-xl border ${
                          darkMode ? "bg-green-500/10 border-green-500/20 text-green-400" : "bg-green-50 border-green-200 text-green-700"
                        }`}>
                          <FiCheckCircle />
                          {agencyPath() === "revenue"    && (isAr ? "✓ مسار الإيرادات — سيُحسب ROAS + الربح" : "✓ Revenue path — ROAS + Profit")}
                          {agencyPath() === "leadgen"    && (isAr ? "✓ مسار توليد العملاء — سيُحسب CPL" : "✓ Lead Gen path — CPL")}
                          {agencyPath() === "conversion" && (isAr ? "✓ مسار التحويلات — سيُحسب CPA" : "✓ Conversion path — CPA")}
                        </div>
                      )}

                      <HintBox>
                        <span className="text-blue-400 font-medium">ROAS = </span>{t("roas_formula_hint")}<br />
                        <span className="text-purple-400 font-medium">CPL = </span>{isAr ? "الإنفاق ÷ العملاء المحتملين" : "Spend ÷ Leads"}<br />
                        <span className="text-orange-400 font-medium">CPA = </span>{isAr ? "الإنفاق ÷ التحويلات" : "Spend ÷ Conversions"}
                      </HintBox>
                    </div>
                  )}

                  {/* ════════════ STORE ════════════ */}
                  {bizType === "store" && (
                    <div className="space-y-4">

                      {/* Revenue — required */}
                      <div>
                        <Label text={t("revenue")} required />
                        <input type="number" value={storeRevenue} onChange={e => setStoreRevenue(e.target.value)} step="0.01" min="0" className={inputCls} placeholder="0 ر.س" required />
                      </div>

                      {/* MarginPct — OPTIONAL */}
                      <div>
                        <Label text={t("profit_margin")} optional />
                        <input type="number" value={margin} onChange={e => setMargin(e.target.value)} step="0.01" min="0" max="100" className={inputCls} placeholder="35.5%" />
                        {!margin ? (
                          <div className="flex items-start gap-2 mt-2 text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 p-2 rounded-lg">
                            <FiInfo className="flex-shrink-0 mt-0.5" />
                            <span>{isAr ? "بدون هامش الربح سيُحسب الـ ROAS فقط ولن يُحتسب الربح" : "Without margin, only ROAS will be computed — Profit not calculated"}</span>
                          </div>
                        ) : (
                          <p className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs mt-1`}>{t("profit_margin_hint")}</p>
                        )}
                      </div>

                      {/* Fees + Shipping + ReturnsPct */}
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label text={t("fees")} optional />
                          <input type="number" value={fees} onChange={e => setFees(e.target.value)} step="0.01" min="0" className={inputCls} placeholder="0 ر.س" />
                        </div>
                        <div>
                          <Label text={t("shipping")} optional />
                          <input type="number" value={shipping} onChange={e => setShipping(e.target.value)} step="0.01" min="0" className={inputCls} placeholder="0 ر.س" />
                        </div>
                        <div>
                          <Label text={t("returns_pct")} optional />
                          <input type="number" value={returnsPct} onChange={e => setReturnsPct(e.target.value)} step="0.01" min="0" max="100" className={inputCls} placeholder="0%" />
                        </div>
                      </div>

                      <HintBox>
                        <span className="text-green-400 font-medium">ROAS = </span>{t("roas_formula_hint")}<br />
                        <span className="text-purple-400 font-medium">{t("profit")} = </span>
                        {isAr
                          ? "الإيراد × الهامش − الإنفاق − الرسوم − الشحن − خسائر الإرجاع"
                          : "Revenue × Margin% − Spend − Fees − Shipping − Returns Loss"}
                      </HintBox>
                    </div>
                  )}

                  {/* ════════════ SERVICE ════════════ */}
                  {bizType === "service" && (
                    <div className="space-y-4">

                      {/* Decision hint */}
                      <div className="bg-orange-500/10 border border-orange-500/20 p-3 rounded-xl flex items-start gap-2 text-xs text-orange-300">
                        <FiInfo className="flex-shrink-0 mt-0.5" />
                        <span>
                          {isAr
                            ? "القرار يعتمد على نسبة تكلفة اكتساب العميل (CAC) مقارنة بمتوسط قيمة الصفقة — وليس ROAS."
                            : "Decision is based on CAC vs deal value ratio — not ROAS."}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label text={t("leads")} required />
                          <input type="number" value={svcLeads} onChange={e => setSvcLeads(e.target.value)} min="0" className={inputCls} placeholder={t("leads_placeholder")} required />
                        </div>
                        <div>
                          {/* UPDATED label */}
                          <Label text={isAr ? "معدل التحويل لعميل دافع %" : "Conversion % (lead → paying client)"} required />
                          <input type="number" value={convPct} onChange={e => setConvPct(e.target.value)} step="0.01" min="0" max="100" className={inputCls} placeholder="15%" required />
                          {convPct && parseFloat(convPct) > 100 && (
                            <p className="text-red-400 text-xs mt-1">{t("error_conversions_exceed_leads")}</p>
                          )}
                          <p className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs mt-1`}>
                            {isAr ? "% من العملاء المحتملين الذين يصبحون عملاء يدفعون" : "% of leads that become paying customers"}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label text={t("avg_ticket")} required />
                          <input type="number" value={avgTicket} onChange={e => setAvgTicket(e.target.value)} step="0.01" min="0" className={inputCls} placeholder="500 ر.س" required />
                          <p className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs mt-1`}>
                            {isAr ? "متوسط قيمة الصفقة لكل عميل دافع" : "Average deal value per paying customer"}
                          </p>
                        </div>
                        <div>
                          <Label text={t("service_cost")} optional />
                          <input type="number" value={serviceCost} onChange={e => setServiceCost(e.target.value)} step="0.01" min="0" className={inputCls} placeholder="0 ر.س" />
                          <p className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs mt-1`}>
                            {isAr ? "تكلفة تقديم الخدمة لكل عميل" : "Cost to serve per customer"}
                          </p>
                        </div>
                      </div>

                      {/* Live CAC preview */}
                      {estCustomers !== null && (
                        <div className={`rounded-xl border p-3 text-xs grid grid-cols-3 gap-3 text-center ${
                          darkMode ? "bg-gray-700/20 border-gray-700 text-gray-300" : "bg-blue-50 border-blue-200 text-gray-700"
                        }`}>
                          <div>
                            <div className={`text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>{estCustomers}</div>
                            <div className={darkMode ? "text-gray-500" : "text-gray-500"}>{isAr ? "عملاء متوقعون" : "Est. Customers"}</div>
                          </div>
                          <div>
                            <div className={`text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
                              {estCAC ? `${estCAC} ر.س` : "—"}
                            </div>
                            <div className={darkMode ? "text-gray-500" : "text-gray-500"}>{isAr ? "تكلفة الاكتساب (CAC)" : "CAC"}</div>
                          </div>
                          <div>
                            <div className={`text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
                              {avgTicket ? `${parseFloat(avgTicket).toFixed(0)} ر.س` : "—"}
                            </div>
                            <div className={darkMode ? "text-gray-500" : "text-gray-500"}>{isAr ? "قيمة الصفقة" : "Deal Value"}</div>
                          </div>
                        </div>
                      )}

                      <HintBox>
                        <span className="text-orange-400 font-medium">CAC = </span>
                        {isAr ? "الإنفاق ÷ العملاء الدافعين" : "Spend ÷ Paying Customers"}<br />
                        <span className="text-blue-400 font-medium">{isAr ? "الربح" : "Profit"} = </span>
                        {isAr ? "الإيراد − الإنفاق − (العملاء × تكلفة الخدمة)" : "Revenue − Spend − (Customers × Service Cost)"}
                      </HintBox>
                    </div>
                  )}

                </motion.div>
              </AnimatePresence>
            </div>

            {/* ── Submit ──────────────────────────────────────── */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className={`flex-1 py-3 rounded-xl transition-colors text-sm ${
                  darkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-900"
                }`}
              >
                {t("cancel")}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-3 rounded-xl font-semibold text-sm transition-colors"
              >
                {loading
                  ? <><FiRefreshCw className="animate-spin" />{t("evaluating")}</>
                  : <><FiTarget />{t("run_decision")}</>
                }
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default EvaluateCampaignModal;